// Fundação compartilhada de acesso à DEK do caso no browser do membro (Trilha 3).
//
// Duas metades vivem aqui, sobre a MESMA costela (`withCaseDek`):
//   - LEITURA: withCaseDek(access, dek => decryptPayload(...))  — extrai o que
//     estava inline em CaseDetailPage (detecção de borda + loadKekMembro + zera KEK).
//   - ESCRITA (Metade B, nova): withCaseDek(access, dek => encryptPayload(...))
//     via `encryptCaseFields` — ata de oitiva, parecer e decisão reusam.
//
// INVARIANTES (ADR-0010 §2.6 / ADR-0012):
//   - roda SÓ no browser do membro (web-comite, zona R-2.6 autorizada);
//   - AAD=caseId em todo encrypt/decrypt (anti-swap cross-caso, CRY-04);
//   - o DEK vive só dentro do callback de withMemberDek (a lib o cria e o zera);
//     aqui só carregamos/zeramos a KEK_membro (kekRaw.fill(0) no finally);
//   - NADA de plaintext nem DEK sobe ao servidor — só ciphertext base64.

import { encryptPayload, decryptPayload, messageAad } from '@aions/crypto';
import { withMemberDek, type MemberDekMaterial } from './member-decrypt.js';
import { loadKekMembro } from './kek-member-session.js';
import { bytesToBase64, base64ToBytes } from './bytes.js';

/** Material privado do caso, do GET /cases/:id — só o que `resolveCaseDekAccess` lê. */
export interface CaseDekInput {
  /** Privada-embrulhada do PRÓPRIO membro (cifrada sob a KEK dele). Flag OFF →
   *  null; ausente em respostas que não projetam o campo. Ambos caem em sem_keypair. */
  myWrappedPrivateKey?: { ciphertext: string; nonce: string } | null;
  /** Wraps RSA do DEK por membro (Fatia 2). Vazio/ausente em caso legado. Cada wrap
   *  carrega a REFERÊNCIA da keypair para a qual foi feito (ausente ⇒ `'origem'`). */
  dekWrappedByMembers?: Array<{
    memberId: string;
    wrappedDek: string;
    keypairRef?: string;
  }> | null;
  /** Referência da keypair VIGENTE do próprio membro, projetada pelo servidor junto
   *  do `myWrappedPrivateKey` (mesma fonte: o UserRecord dele). Ausente ⇒ `'origem'`. */
  myKeypairRef?: string | null;
}

/** Wraps/keypairs sem referência são anteriores à maquinaria de geração e contam como
 *  a MESMA referência — back-compat: o acervo atual não vira obsoleto. */
const REF_ORIGEM = 'origem';

/**
 * Resultado da resolução de acesso à DEK. As 3 bordas honestas (ADR-0012 §2.3,
 * ordem travada) + o caminho `ok` que carrega o material pronto pra withCaseDek
 * (tudo menos a kekRaw, injetada por withCaseDek via loadKekMembro).
 *
 * `sem_keypair` é o catch-all calmo: cobre a flag OFF (servidor devolve
 * myWrappedPrivateKey=null) E o membro que ainda não tem keypair.
 */
export type CaseDekAccess =
  | { status: 'ok'; material: Omit<MemberDekMaterial, 'kekRaw'> }
  | { status: 'sem_keypair' | 'caso_legado' | 'sem_acesso' };

/**
 * PURO (sem async, sem cripto). Espelha a detecção de borda de CaseDetailPage,
 * na ordem travada (ADR-0012 §2.3): sem_keypair → caso_legado → sem_acesso → ok.
 * Reusável por leitura E escrita; testável sem WebCrypto.
 */
export function resolveCaseDekAccess(data: CaseDekInput, memberId: string): CaseDekAccess {
  // (1) sem a privada-embrulhada → sem_keypair (cobre flag OFF e membro sem keypair).
  if (!data.myWrappedPrivateKey) return { status: 'sem_keypair' };
  // (2) nenhum wrap por membro → caso_legado (anterior à cripto de leitura).
  const wraps = data.dekWrappedByMembers;
  if (!wraps || wraps.length === 0) return { status: 'caso_legado' };
  // (3) sem wrap UTILIZÁVEL → sem_acesso (ingressei após o submit; Fatia 4 — OU a
  //     minha keypair foi invalidada e o wrap que existe é para a antiga).
  //
  //     ⚠️ PRESENÇA NÃO É VALIDADE, do lado da LEITURA (28/07). Com append-only, o
  //     mesmo membro pode ter DOIS wraps (o morto fica). Casar só por memberId pegava
  //     o PRIMEIRO — o morto — e o membro caía em `falha_decrypt`, que manda falar com
  //     o administrador. A borda honesta aqui é `sem_acesso`: é exatamente o que a copy
  //     dela descreve (o acesso é liberado quando a chave for redistribuída), e é o que
  //     o self-heal vai fazer. Este é o 4º dos 4 filtros que espelham esta regra —
  //     os outros 3 são de ESCRITA (cliente/servidor/repo).
  const minhaRef = data.myKeypairRef ?? REF_ORIGEM;
  const myWrap = wraps.find(
    (m) => m.memberId === memberId && (m.keypairRef ?? REF_ORIGEM) === minhaRef,
  );
  if (!myWrap) return { status: 'sem_acesso' };
  // (4) caminho feliz — material pronto (kekRaw vem depois, em withCaseDek).
  return {
    status: 'ok',
    material: {
      privateKeyCiphertext: data.myWrappedPrivateKey.ciphertext,
      privateKeyNonce: data.myWrappedPrivateKey.nonce,
      wrappedDek: myWrap.wrappedDek,
    },
  };
}

/**
 * Abre a DEK do caso e a entrega ao callback `fn`. Encapsula o boilerplate que
 * estava inline em CaseDetailPage: loadKekMembro → withMemberDek (que cria e zera
 * o DEK) → kekRaw.fill(0) no finally (mesmo em erro). O DEK NUNCA escapa do escopo
 * de `fn`; a KEK_membro é zerada aqui. Lança se a leitura falhar (KEK ausente,
 * tag AES-GCM fail, RSA unwrap fail) — o caller mapeia pra borda `falha_decrypt`.
 */
export async function withCaseDek<T>(
  access: Extract<CaseDekAccess, { status: 'ok' }>,
  fn: (dek: Uint8Array) => Promise<T>,
): Promise<T> {
  let kekRaw: Uint8Array | null = null;
  try {
    kekRaw = await loadKekMembro();
    return await withMemberDek({ ...access.material, kekRaw }, fn);
  } finally {
    kekRaw?.fill(0); // a lib já zerou o DEK; aqui zeramos a KEK, mesmo em erro.
  }
}

/** Um campo cifrado pronto pro wire (base64), igual ao que o backend já espera. */
export interface EncryptedField {
  ciphertext: string;
  nonce: string;
}

/**
 * Metade B — cifra N campos com a DEK do caso numa ÚNICA abertura (ata+presentes
 * sem reabrir a DEK duas vezes). Cada campo recebe nonce fresco (encryptPayload
 * gera internamente) e AAD=caseId. Retorna um record com as mesmas chaves de
 * entrada, cada uma com {ciphertext, nonce} em base64.
 */
export async function encryptCaseFields<K extends string>(
  access: Extract<CaseDekAccess, { status: 'ok' }>,
  caseId: string,
  fields: Record<K, string>,
): Promise<Record<K, EncryptedField>> {
  return withCaseDek(access, async (dek) => {
    const out = {} as Record<K, EncryptedField>;
    for (const key of Object.keys(fields) as K[]) {
      const { ciphertext, nonce } = await encryptPayload(fields[key], dek, caseId);
      out[key] = { ciphertext: bytesToBase64(ciphertext), nonce: bytesToBase64(nonce) };
    }
    return out;
  });
}

// ---------------------------------------------------------------------------
// Divergência 3 (canal de mensagens comitê↔denunciante) — cifra/decifra sob a
// DEK do caso com AAD=caseId+ulid (messageAad, fonte única em @aions/crypto).
// Distinto de encryptCaseFields (AAD=caseId): o ulid amarra a mensagem à sua
// posição (anti-reordenação/replay). O DEK vive só dentro de withCaseDek.
// ---------------------------------------------------------------------------

/** Uma mensagem cifrada do thread (como vem do GET /cases/:id). */
export interface CaseMessageInput {
  ulid: string;
  ciphertext: string;
  nonce: string;
  createdWeek: string;
  /** Data e hora — só nas mensagens de ator identificado (comitê/painel/IA).
   *  Ausente nas do denunciante, por anonimato (`selarMensagem`, core). */
  createdAtEpochMs?: number;
  origin?: string;
}

/** Uma mensagem decifrada, pronta pra render (texto claro só no browser). */
export interface DecryptedCaseMessage {
  ulid: string;
  createdWeek: string;
  /** Repassado EXPLICITAMENTE no map de `decryptCaseMessages`: aquele objeto é
   *  montado campo por campo e o que não for copiado desaparece sem erro —
   *  o modo de falha do `keypairRef`. */
  createdAtEpochMs?: number;
  origin?: string;
  text: string;
}

/** Marcador de falha por-item (não derruba o thread inteiro). */
export const FALHA_DECRYPT = '[falha ao decifrar]';

/**
 * Cifra UMA mensagem sob a DEK do caso, AAD=messageAad(caseId, ulid). O `ulid` é
 * gerado no browser ANTES de cifrar e viaja no POST — o denunciante recompõe a
 * MESMA AAD pelo ulid persistido. Nonce fresco (encryptPayload gera). Retorna
 * {ciphertext, nonce} base64, prontos pro wire.
 */
export async function encryptCaseMessage(
  access: Extract<CaseDekAccess, { status: 'ok' }>,
  caseId: string,
  ulid: string,
  plaintext: string,
): Promise<EncryptedField> {
  return withCaseDek(access, async (dek) => {
    const { ciphertext, nonce } = await encryptPayload(plaintext, dek, messageAad(caseId, ulid));
    return { ciphertext: bytesToBase64(ciphertext), nonce: bytesToBase64(nonce) };
  });
}

/**
 * Decifra o thread de mensagens Div3 numa ÚNICA abertura da DEK. Cada item usa
 * AAD=messageAad(caseId, ulid). Item sem ciphertext (placeholder vazio) é pulado;
 * falha por-item vira FALHA_DECRYPT (não derruba os demais). Ordem preservada.
 */
export async function decryptCaseMessages(
  access: Extract<CaseDekAccess, { status: 'ok' }>,
  caseId: string,
  messages: ReadonlyArray<CaseMessageInput>,
): Promise<DecryptedCaseMessage[]> {
  return withCaseDek(access, async (dek) => {
    const out: DecryptedCaseMessage[] = [];
    for (const m of messages) {
      if (!m.ciphertext) continue; // placeholder vazio (ex.: msg-0 legado)
      let text: string;
      try {
        text = await decryptPayload(
          base64ToBytes(m.ciphertext),
          base64ToBytes(m.nonce),
          dek,
          messageAad(caseId, m.ulid),
        );
      } catch {
        text = FALHA_DECRYPT;
      }
      out.push({
        ulid: m.ulid,
        createdWeek: m.createdWeek,
        text,
        ...(m.createdAtEpochMs !== undefined ? { createdAtEpochMs: m.createdAtEpochMs } : {}),
        ...(m.origin !== undefined ? { origin: m.origin } : {}),
      });
    }
    return out;
  });
}
